import type { LayerData } from '@/atoms/layers.atom';
import type { RunCompareSurfacesDialogProps } from '@/components/RunCompareSurfacesDialog';
import { PC_METHODS } from '@/components/SurfacePicker';
import { ProcessJobType } from '@/graphql/graphql';
import { defaultRampName } from '@/lib/cutFillConvention';
import { DEFAULT_MIN_COVERAGE_PCT, type StationVolumesInitialParams } from '@/lib/stationVolumes/dialogModel';
import { EXPECTATION_MODES, type ExpectationForm } from '@/lib/surfaceExpectation';

import { allowedDeviationModes, COMPARE_KINDS, COMPARE_RESULTS, type CompareResult } from './compare/availability';
import { defaultReferenceMode } from './compare/deviationCoupling';
import {
  CUT_FILL_GRID_DEFAULT_M,
  DEVIATION_RANGE_DEFAULT_M,
  LEGACY_PC_METHOD,
  NEW_RUN_PC_METHOD,
  type ComparePatch,
  type CompareFormValues,
  type PcMethod,
} from './compare/formSchema';
import type { AnalysisId } from './types';

export type CompareRerunNotice = NonNullable<RunCompareSurfacesDialogProps['rerunNotice']>;

/**
 * How a past run re-opens (D12). Compare surfaces carries the full parameter
 * set of either engine, mapped onto the merged form (spec §4.3); the station
 * dock's re-runs go through the same mapping, for the same defaults. The
 * other dialogs have no initial-values prop, so their re-run is "same
 * source, fresh form". `missingInputs` = the run named a layer that no
 * longer exists.
 */
export type RerunPlan =
  | {
      id: 'compare';
      initialValues: ComparePatch;
      /** Volumes by chainage, always on the cut & fill side. */
      initialStationParams?: StationVolumesInitialParams;
      rerunNotice?: CompareRerunNotice;
      missingInputs: boolean;
    }
  | { id: 'landxmlMerge'; sourceLayerIds: string[]; outputName?: string; missingInputs: boolean }
  | {
      id: 'clearance' | 'groundClassify' | 'mesh' | 'rainfall' | 'landxmlExport' | 'iprjExport';
      sourceLayer?: LayerData;
      missingInputs: boolean;
    };

export type CompareRerunPlan = Extract<RerunPlan, { id: 'compare' }>;

const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);
const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const oneOf = <T extends string>(options: readonly T[], v: unknown): T | undefined =>
  options.find((o) => o === v);
const pcMethodOf = (v: unknown): PcMethod | undefined => oneOf(PC_METHODS, v);
/** Metres → millimetres without float noise (0.15 m → 150, not 150.00000000000003). */
const mm = (m: number | undefined): number | null => (m == null ? null : Math.round(m * 1e6) / 1e3);

type Find = (layerId: string | undefined) => LayerData | undefined;

const finder = (layers: readonly LayerData[]) => {
  const find: Find = (layerId) => (layerId ? layers.find((l) => l.id === layerId) : undefined);
  const lost = (layerId: string | undefined) => !!layerId && !find(layerId);
  return { find, lost };
};

/** Drops the keys a mapping left `undefined`, so a patch never names a field it
 *  does not restore. */
const defined = <T extends Record<string, unknown>>(o: T): Partial<T> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;

/**
 * `inputJson.stationVolumes` (preprocess's scalar echo) as the dialog's
 * station pre-fill. A run without the four fields a station section cannot
 * do without has none to restore. The echo carries no crossfall check.
 */
function stationParamsOf(echo: unknown): StationVolumesInitialParams | undefined {
  const sv = asRecord(echo);
  const alignmentKey = str(sv.alignmentKey);
  const stationInterval = num(sv.stationIntervalM);
  const swathLeft = num(sv.swathLeftM);
  const swathRight = num(sv.swathRightM);
  if (!alignmentKey || stationInterval == null || swathLeft == null || swathRight == null) return undefined;
  return {
    alignmentKey,
    stationInterval,
    swathLeft,
    swathRight,
    offsetBin: num(sv.offsetBinM) ?? 0.5,
    minCoveragePct: num(sv.minCoveragePct) ?? DEFAULT_MIN_COVERAGE_PCT,
    ...defined({ stationFrom: num(sv.stationFromInternalM), stationTo: num(sv.stationToInternalM) }),
  };
}

/**
 * `inputJson.expectation` (stored in metres) as the form's root expectation
 * fields (millimetres). A SURFACES run stored only its surfaces' S3 keys, so
 * the two layers are left for the user to pick again; their sampling and the
 * tolerances come back.
 */
function expectationOf(stored: unknown): Partial<ExpectationForm> | undefined {
  const e = asRecord(stored);
  const mode = oneOf(EXPECTATION_MODES, e.mode);
  if (!mode || mode === 'NONE') return undefined;
  return {
    expectMode: mode,
    expectThicknessMm: mode === 'CONSTANT' ? mm(num(e.thicknessM)) : null,
    ...(mode === 'SURFACES'
      ? {
          expectUpperPcMethod: pcMethodOf(e.upperPcMethod) ?? 'min',
          expectLowerPcMethod: pcMethodOf(e.lowerPcMethod) ?? 'min',
        }
      : {}),
    underTolMm: mm(num(e.underToleranceM)),
    overTolMm: mm(num(e.overToleranceM)),
    failAreaPct: num(e.failAreaPct) ?? 5,
  };
}

/** One engine's input mapped onto the merged form, before results are picked. */
interface Half {
  patch: ComparePatch;
  stationParams?: StationVolumesInitialParams;
  expectation?: Partial<ExpectationForm>;
  missingInputs: boolean;
}

/** A SURFACE_DIFFERENCE input: role A is `top`, B is `bottom` in both modes. */
function surfaceDifferenceHalf(input: unknown, layers: readonly LayerData[]): Half & { legacyMin: boolean } {
  const inp = asRecord(input);
  const params = asRecord(inp.params);
  const { find, lost } = finder(layers);
  const topId = str(inp.topLayerId);
  const bottomId = str(inp.bottomLayerId);
  const top = find(topId)?.id;
  const bottom = find(bottomId)?.id;
  // A run made before the sampling was stored used Lowest point (spec §10.2).
  const topPcMethod = pcMethodOf(inp.topPcMethod) ?? LEGACY_PC_METHOD;
  const bottomPcMethod = pcMethodOf(inp.bottomPcMethod) ?? LEGACY_PC_METHOD;
  const outputName = str(inp.outputName);
  return {
    patch: {
      kind: oneOf(COMPARE_KINDS, params.compareKind) ?? 'design',
      inputs: {
        ...(top ? { topLayerId: top, topSurfaceIds: strArr(inp.topSurfaceIds) } : {}),
        ...(bottom ? { bottomLayerId: bottom, bottomSurfaceIds: strArr(inp.bottomSurfaceIds) } : {}),
        topPcMethod,
        bottomPcMethod,
      },
      cutFill: {
        resolution: num(params.resolution) ?? CUT_FILL_GRID_DEFAULT_M,
        // Cut is the NEGATIVE side of dz = top − bottom (T4).
        colormap: str(params.colormap) ?? defaultRampName('surfaceDifference'),
        fillHoles: num(params.fillHoles) ?? 0,
      },
      // GeoTIFFs are opt-out: a run before the switch made them.
      addOns: { dxf: bool(params.exportDxf) ?? false, geotiff: bool(params.exportTif) ?? true },
      ...(outputName ? { output: { name: outputName } } : {}),
    },
    stationParams: stationParamsOf(inp.stationVolumes),
    expectation: expectationOf(inp.expectation),
    missingInputs: lost(topId) || lost(bottomId),
    legacyMin: topPcMethod === 'min' || bottomPcMethod === 'min',
  };
}

/** A DEVIATION input: the reference is the design (A), the source the
 *  as-built (B). */
function deviationHalf(input: unknown, layers: readonly LayerData[]): Half {
  const inp = asRecord(input);
  const params = asRecord(inp.params);
  const { find, lost } = finder(layers);
  const sourceId = str(inp.sourceLayerId);
  const referenceId = str(inp.referenceLayerId);
  const designLayer = find(referenceId);
  const reference = designLayer?.id;
  const source = find(sourceId)?.id;
  const referenceMode = oneOf(['tin', 'surfaces'] as const, params.referenceMode);
  const allowedModes = allowedDeviationModes(designLayer);
  const outputName = str(inp.outputName);
  // Preprocess stamps the project's SRID into `tifSrid` when the user left it
  // blank (and echoes it as `projectSrid`): that one was never picked, so it
  // is not restored — a later change of the project SRID must still apply.
  const tifSrid = num(params.tifSrid);
  const pickedTifSrid = tifSrid != null && tifSrid !== num(params.projectSrid) ? tifSrid : undefined;
  const deviation: Partial<CompareFormValues['deviation']> = {
    // The fields the old Analyses-page row restored, with its defaults.
    // A mode the restored design allows; a missing, unknown or disallowed one
    // becomes the design's first (vertical; nearest for IFC). The dialog's
    // coupling still guarantees it (R25).
    mode: oneOf(allowedModes, params.mode) ?? allowedModes[0],
    min: num(params.min) ?? -DEVIATION_RANGE_DEFAULT_M,
    max: num(params.max) ?? DEVIATION_RANGE_DEFAULT_M,
    decimate: num(params.decimate) ?? 1,
    // `params.colormap` has no field: the merged form never sends it.
    renderColormap: str(params.renderColormap) ?? defaultRampName('deviation'),
    verticalThreshold: num(params.verticalThreshold) ?? 0.25,
    kNearestTriangles: num(params.kNearestTriangles) ?? 16,
    signed: bool(params.signed) ?? true,
    ifcName: str(params.ifcName) ?? '',
    ifcTypes: str(params.ifcTypes) ?? '',
    // The rest only when the run stored them; otherwise the form's defaults
    // (and the design couplings) apply.
    ...defined({
      volumeTolerance: num(params.volumeTolerance),
      tifResolution: num(params.tifResolution),
      tifStat: oneOf(['mean', 'min', 'max', 'count'] as const, params.tifStat),
      tifSrid: pickedTifSrid,
      renderPerSurface: bool(params.renderPerSurface),
      renderImageSize: num(params.renderImageSize),
      volumeStats: bool(params.volumeStats),
      volumeResolution: num(params.volumeResolution),
      volumePlane: oneOf(['xy', 'fitted'] as const, params.volumePlane),
      // Preprocess always stores the mode it RESOLVED, so a stored mode is the
      // user's own only when it differs from the design type's default (R24):
      // then it is flagged edited, sent, and kept across a design change; a
      // default one is not, so it re-defaults when the design changes.
      referenceMode,
      ...(referenceMode && designLayer && referenceMode !== defaultReferenceMode(designLayer)
        ? { referenceModeEdited: true }
        : {}),
      referenceResolution: num(params.referenceResolution),
      referenceCellStat: oneOf(['min', 'mean', 'median', 'p05'] as const, params.referenceCellStat),
      referenceInterp: oneOf(['nearest', 'bilinear'] as const, params.referenceInterp),
    }),
  };
  return {
    patch: {
      kind: 'design',
      inputs: {
        ...(reference ? { topLayerId: reference } : {}),
        ...(source ? { bottomLayerId: source } : {}),
        // A deviation has no cut & fill sampling to keep: one ticked on this
        // re-run is a new analysis and starts from the new-run default (R23).
        topPcMethod: NEW_RUN_PC_METHOD,
        bottomPcMethod: NEW_RUN_PC_METHOD,
      },
      deviation,
      addOns: { geotiff: bool(params.outTif) ?? true },
      ...(outputName ? { output: { name: outputName } } : {}),
    },
    stationParams: stationParamsOf(inp.stationVolumes),
    expectation: expectationOf(inp.expectation),
    missingInputs: lost(sourceId) || lost(referenceId),
  };
}

/** `b` over `a`, group by group (a ComparePatch's groups are partial). */
function mergePatches(a: ComparePatch, b: ComparePatch): ComparePatch {
  const merged: Record<string, unknown> = { ...a, ...b };
  for (const group of ['inputs', 'cutFill', 'deviation', 'addOns', 'output'] as const) {
    if (a[group] || b[group]) merged[group] = { ...a[group], ...b[group] };
  }
  return merged as ComparePatch;
}

function comparePlan(
  patch: ComparePatch,
  results: readonly CompareResult[],
  half: Pick<Half, 'stationParams' | 'expectation' | 'missingInputs'>,
  rerunNotice: CompareRerunNotice | undefined,
): CompareRerunPlan {
  return {
    id: 'compare',
    missingInputs: half.missingInputs,
    initialValues: {
      ...patch,
      results: COMPARE_RESULTS.filter((r) => results.includes(r)),
      ...half.expectation,
    },
    ...(half.stationParams ? { initialStationParams: half.stationParams } : {}),
    ...(rerunNotice ? { rerunNotice } : {}),
  };
}

/** A single legacy run: only its own result is ticked (spec §4.3). */
function compareRerunPlan(input: unknown, layers: readonly LayerData[], jobType?: ProcessJobType) {
  if (jobType === ProcessJobType.SurfaceDifference) {
    const sd = surfaceDifferenceHalf(input, layers);
    return comparePlan(sd.patch, ['cutFill'], sd, sd.legacyMin ? 'legacyMinSampling' : undefined);
  }
  if (jobType === ProcessJobType.Deviation) {
    const dev = deviationHalf(input, layers);
    // Volumes by chainage and Expected thickness now come only from cut &
    // fill (spec goal 3, §4.3 builder's call): a deviation that used either
    // re-opens with both results, those params on the cut & fill side. The
    // notice names what moved; stations win when both did.
    const notice: CompareRerunNotice | undefined = dev.stationParams
      ? 'stationsMovedToCutFill'
      : dev.expectation
        ? 'expectationMovedToCutFill'
        : undefined;
    return comparePlan(dev.patch, notice ? ['cutFill', 'deviation'] : ['deviation'], dev, notice);
  }
  return null;
}

/**
 * A paired run's two halves as one form: the inputs, kind, sampling and
 * stations from the cut & fill half (the authoritative one — it alone carries
 * surface picks and sampling), the deviation params from the deviation half.
 */
function pairedPlan(sdInput: unknown, devInput: unknown, layers: readonly LayerData[], results: CompareResult[]) {
  const sd = surfaceDifferenceHalf(sdInput, layers);
  const dev = deviationHalf(devInput, layers);
  return comparePlan(
    mergePatches(dev.patch, sd.patch),
    results,
    {
      stationParams: sd.stationParams,
      expectation: sd.expectation,
      missingInputs: sd.missingInputs || dev.missingInputs,
    },
    sd.legacyMin ? 'legacyMinSampling' : undefined,
  );
}

/** "Re-run both" on a paired run (hub, Task 12). */
export const buildPairedRerunPlan = (sdInput: unknown, devInput: unknown, layers: readonly LayerData[]) =>
  pairedPlan(sdInput, devInput, layers, ['cutFill', 'deviation']);

/** "Re-run cut & fill only" / "Re-run deviation map only" on a paired run: only
 *  that part is ticked, the other half's values stay behind it (Task 12). */
export const buildPartRerunPlan = (
  part: CompareResult,
  sdInput: unknown,
  devInput: unknown,
  layers: readonly LayerData[],
) => pairedPlan(sdInput, devInput, layers, [part]);

/**
 * A paired run with only ONE readable half — the other part failed to start,
 * so no job holds its input (its hub row is the batch, not a job). The form
 * is seeded from that half: the shared values (inputs, compare mode, GeoTIFF,
 * name) are the run's, and the unreadable side starts from the dialog's own
 * defaults, exactly as a fresh tick of that result would. `results` are
 * ticked as asked — "Re-run both" really re-runs both.
 */
export function buildHalfRerunPlan(
  readable: CompareResult,
  input: unknown,
  layers: readonly LayerData[],
  results: readonly CompareResult[],
): CompareRerunPlan {
  if (readable === 'cutFill') {
    const sd = surfaceDifferenceHalf(input, layers);
    return comparePlan(sd.patch, results, sd, sd.legacyMin ? 'legacyMinSampling' : undefined);
  }
  const dev = deviationHalf(input, layers);
  // As for a single deviation re-run: stations / an expectation move onto
  // cut & fill, which is then ticked too.
  const notice: CompareRerunNotice | undefined = dev.stationParams
    ? 'stationsMovedToCutFill'
    : dev.expectation
      ? 'expectationMovedToCutFill'
      : undefined;
  return comparePlan(dev.patch, notice ? [...results, 'cutFill'] : results, dev, notice);
}

/**
 * `jobType` picks the engine a `compare` run came from (the hub row knows
 * it); without a compare job type there is no plan and the caller opens a
 * fresh dialog.
 */
export function buildRerunPlan(
  id: AnalysisId,
  input: unknown,
  layers: readonly LayerData[],
  jobType?: ProcessJobType,
): RerunPlan | null {
  const inp = asRecord(input);
  const { find, lost } = finder(layers);

  switch (id) {
    case 'compare':
      return compareRerunPlan(input, layers, jobType);
    case 'clearance': {
      const pc = str(inp.pointcloudLayerId);
      return { id, sourceLayer: find(pc), missingInputs: lost(pc) };
    }
    case 'landxmlMerge': {
      const ids = strArr(inp.sourceLayerIds);
      return {
        id,
        sourceLayerIds: ids.filter((layerId) => !!find(layerId)),
        outputName: str(inp.outputName),
        missingInputs: ids.some((layerId) => lost(layerId)),
      };
    }
    case 'groundClassify':
    case 'mesh':
    case 'rainfall':
    case 'landxmlExport':
    case 'iprjExport': {
      const source = str(inp.sourceLayerId);
      return { id, sourceLayer: find(source), missingInputs: lost(source) };
    }
    case 'stockpile':
    case 'crestLine':
      return null;
  }
}
